import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from "recharts";
import { TrendingDown, Crown, Activity, Target, ArrowRight, Coins, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { levelFromXp, getClass } from "@/lib/rpg";
import { card, label, btnPrimary, btnSecondary, tag } from "@/lib/styles";

const TIER_TITLES = ["Orientation", "Foundations", "Retrieval & RAG", "Reliability", "Agents & Design"];
const TIER_SHORT = ["T0", "T1", "T2", "T3", "T4"];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="min-h-[60vh] flex items-center justify-center text-gold font-mono"><span className="cursor-blink">loading</span></div>;

  const history = d.history.map((h, i) => ({ n: i + 1, score: h.score }));
  const empty = d.total_graded === 0;
  const lvl = levelFromXp(d.xp || 0);
  const klass = getClass(d.character_class);
  const radarData = [0, 1, 2, 3, 4].map((t) => {
    const ta = d.tier_avgs.find((x) => x.tier === t);
    return { tier: TIER_SHORT[t], mastery: ta ? ta.avg : 0 };
  });

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className={label}>character sheet</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Your legend</h1>

        {/* CHARACTER BANNER */}
        <div className={`${card} p-6 mt-6 flex flex-col md:flex-row md:items-center gap-6`} data-testid="character-sheet">
          <div className="flex items-center gap-4 shrink-0">
            {klass
              ? <img src={klass.sigil} alt="" className="w-20 h-20 select-none" style={{ WebkitMaskImage: "radial-gradient(circle,#000 60%,transparent 72%)", maskImage: "radial-gradient(circle,#000 60%,transparent 72%)" }} />
              : <div className="w-20 h-20 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center"><Sparkles className="text-gold" /></div>}
            <div>
              <p className="font-serif font-bold text-2xl text-zinc-100">{user?.name || "Adventurer"}</p>
              <p className={`text-sm ${klass?.tint || "text-gold"}`}>{klass ? `${klass.name} · ${klass.title}` : "Classless"}</p>
            </div>
          </div>
          <div className="flex-1 w-full">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-serif font-bold text-gold" data-testid="char-rank">{lvl.rank} · Level {lvl.level}</span>
              <span className="text-zinc-500 text-xs">{lvl.into} / {lvl.need} XP</span>
            </div>
            <div className="h-3 bg-black/40 rounded-full border border-zinc-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-gem to-gem-light gem-glow rounded-full transition-all" style={{ width: `${lvl.pct}%` }} data-testid="xp-bar" />
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold border border-gold/30 bg-gold/5 px-3 py-1.5 rounded-md" data-testid="char-gold"><Coins size={15} /> {d.gold || 0} gold</span>
              <span className={tag}>{d.topics_studied || 0} topics studied</span>
              <span className={tag}>{d.total_graded} answers graded</span>
              <span className={tag}>{d.plan === "pro" ? "Pro · unlimited duels" : `${d.trial_interviews_used}/${d.trial_limit} trial duels`}</span>
            </div>
          </div>
        </div>

        {/* stat row */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 rounded-lg overflow-hidden">
          {[
            { k: "duels", v: d.total_interviews, sub: "interviews run" },
            { k: "graded", v: d.total_graded, sub: "answers graded" },
            { k: "avg score", v: d.avg_score ?? "N/A", sub: "across all answers" },
            { k: "gold", v: d.gold || 0, sub: "earned total" },
          ].map((s) => (
            <div key={s.k} className="bg-zinc-950 p-5" data-testid={`stat-${s.k.replace(" ", "-")}`}>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</p>
              <p className="font-sans font-bold text-3xl text-gold glow-gold mt-1">{s.v}</p>
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.sub}</p>
            </div>
          ))}
        </div>

        {d.plan !== "pro" && (
          <Link to="/pricing" data-testid="dashboard-upgrade" className="mt-4 flex items-center justify-between border border-yellow-500/40 bg-yellow-950/10 p-4 hover:bg-yellow-950/20 transition-colors">
            <span className="text-sm text-yellow-300 flex items-center gap-2"><Crown size={16} /> {d.trial_interviews_used >= d.trial_limit ? "Trial used up. Go Pro for unlimited interviews." : `${d.trial_limit - d.trial_interviews_used} free interview(s) left. Upgrade for unlimited.`}</span>
            <ArrowRight size={16} className="text-yellow-400" />
          </Link>
        )}

        {empty ? (
          <div className="mt-10 border border-zinc-800 bg-zinc-950 p-12 text-center">
            <Activity className="mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-400">No graded answers yet. Pick a topic and face Altaris.</p>
            <Link to="/curriculum" className={`${btnPrimary} mt-5`}>Go to curriculum</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* score history */}
            <div className={`${card} p-6`}>
              <span className={label}>score history</span>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history} margin={{ left: -20, right: 10, top: 10 }}>
                    <CartesianGrid stroke="#252f4c" strokeDasharray="3 3" />
                    <XAxis dataKey="n" stroke="#73809d" fontSize={11} />
                    <YAxis domain={[0, 100]} stroke="#73809d" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#0e1626", border: "1px solid #252f4c", borderRadius: 6, fontFamily: "Hanken Grotesk", fontSize: 12 }} />
                    <Line type="monotone" dataKey="score" stroke="#e6b84a" strokeWidth={2} dot={{ fill: "#e6b84a", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* mastery radar */}
            <div className={`${card} p-6`}>
              <span className={label}>mastery web</span>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid stroke="#252f4c" />
                    <PolarAngleAxis dataKey="tier" tick={{ fill: "#95a1bd", fontSize: 12 }} />
                    <Radar dataKey="mastery" stroke="#e6b84a" fill="#e6b84a" fillOpacity={0.35} />
                    <Tooltip contentStyle={{ background: "#0e1626", border: "1px solid #252f4c", borderRadius: 6, fontFamily: "Hanken Grotesk", fontSize: 12 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* drill these next */}
        {d.weakest.length > 0 && (
          <div className="mt-8">
            <span className={label}>active quests · weakest tiers</span>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              {d.weakest.map((w) => (
                <div key={w.tier} className="bg-zinc-950 p-5" data-testid={`weakest-tier-${w.tier}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Tier {w.tier}</span>
                    <TrendingDown size={14} className="text-red-400" />
                  </div>
                  <p className="font-sans font-bold mt-1">{TIER_TITLES[w.tier]}</p>
                  <p className="text-2xl font-black text-red-400 mt-2">{w.avg}<span className="text-xs text-zinc-600">/100</span></p>
                  <Link to="/curriculum" className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-emerald-400 hover:underline" data-testid="dashboard-weakest-topic-link"><Target size={12} /> review topics</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* recent sessions */}
        {d.sessions.length > 0 && (
          <div className="mt-8">
            <span className={label}>recent interviews</span>
            <div className="mt-3 border border-zinc-800">
              {d.sessions.map((s) => (
                <div key={s.session_id} className="flex items-center justify-between gap-4 border-b border-zinc-800 last:border-0 p-4 bg-zinc-950" data-testid={`session-row-${s.session_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{s.topic_title}</p>
                    <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Tier {s.tier} · {s.questions} question(s)</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={tag}>best {s.best_score ?? "N/A"}</span>
                    <span className={tag}>avg {s.avg_score ?? "N/A"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
